/**
 * 1.8.2 — the events calendar's grid on /events (D-1.8.2-2): a real table, the weekdays as column headers, the weeks as
 * rows, a cell a day. In each cell the day is the cell's own header — a button that names the day and how many events
 * it has (a tap opens that day's events under the grid) — and each event is a short line that is a link to its page,
 * described by the day it is on. Today says so in a word. An open day is pressed (`aria-pressed`), never colour alone.
 *
 * Keyboard: one day button is in the tab order (today, the day open, or the 1st); ← → move a day, ↑ ↓ a week, Home /
 * End to the week's Sunday / Saturday, Page Up / Page Down a month — across the month's edge the page turns the month
 * (`onMove`). Enter or Space opens the day. A day that has not passed has a quiet "+" (New event on that day) for the
 * pointer; the keyboard has the same press in the open day's "New event on this day".
 *
 * The placement is `shared/events-grid.ts`'s (a repeat drawn on every day it lands, because each of its events is its
 * own). Large print is respected: sizes are in rem, a line wraps to two lines before it is cut.
 */
import { useEffect, type KeyboardEvent } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { clock } from '@shared/event-calendar';
import type { EventView } from '@shared/events';
import { GRID_KEYS, monthGrid, moveDay } from '@shared/events-grid';
import { Icon } from '@/components/Icons';
import { formatDayKey } from '@/lib/dates';
import { weekdayNames } from './month/days';

/** How many lines a cell shows before "+n more". */
const SHOWN = 3;

export interface EventsGrid182Props {
  month: string;
  monthLabel: string;
  events: readonly EventView[];
  todayKey: string;
  /** The day open under the grid, or null. */
  selected: string | null;
  /** The day the keyboard is on (may be in another month while the page turns). */
  focusDay: string | null;
  /** Bumped when the keyboard moved: the grid then puts the focus on `focusDay`. */
  focusTick: number;
  onPick: (dayKey: string) => void;
  /** The keyboard moved to this day (maybe in another month). */
  onMove: (dayKey: string) => void;
  /** New event on that day, or null for a role that may not make events. */
  onAdd: ((dayKey: string, opener: HTMLElement) => void) | null;
}

export function EventsGrid182({ month, monthLabel, events, todayKey, selected, focusDay, focusTick, onPick, onMove, onAdd }: EventsGrid182Props) {
  const { t, i18n } = useTranslation();
  const names = weekdayNames(i18n.language);
  const weeks = monthGrid(month, events);
  const inMonth = (k: string | null): k is string => !!k && k.startsWith(month);
  const tabDay = inMonth(focusDay) ? focusDay : inMonth(selected) ? selected : todayKey.startsWith(month) ? todayKey : `${month}01`;

  useEffect(() => {
    if (!focusTick || !inMonth(focusDay)) return;
    document.querySelector<HTMLButtonElement>(`[data-testid="eg-daybtn-${focusDay}"]`)?.focus();
    // only a keyboard move asks for the focus
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusTick, month]);

  const onKey = (dayKey: string) => (e: KeyboardEvent<HTMLButtonElement>) => {
    if (!(GRID_KEYS as readonly string[]).includes(e.key)) return;
    const to = moveDay(dayKey, e.key);
    if (!to) return;
    e.preventDefault();
    onMove(to);
  };

  return (
    <div className="ev182-gridwrap">
      <table className="ev182-grid" data-testid="events-grid" aria-describedby="ev182-keys">
        <caption className="sr-only">{t('ev182.cal.caption', { month: monthLabel })}</caption>
        <thead>
          <tr>
            {names.map((n) => (
              <th key={n.long} scope="col" abbr={n.long}>
                <span aria-hidden="true">{n.short}</span>
                <span className="sr-only">{n.long}</span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {weeks.map((week, w) => (
            <tr key={w}>
              {week.map((cell, d) => {
                const dayKey = cell.dayKey;
                if (!dayKey) return <td key={`out-${d}`} className="is-out" />;
                const today = dayKey === todayKey;
                const open = dayKey === selected;
                const dayWords = formatDayKey(dayKey, i18n.language);
                const extra = cell.events.length - SHOWN;
                return (
                  <td key={dayKey} className={`${today ? 'is-today' : ''}${open ? ' is-open' : ''}`.trim() || undefined} aria-current={today ? 'date' : undefined} data-testid={`eg-day-${dayKey}`}>
                    <div className="ev182-cellhead">
                      <button
                        type="button"
                        className="ev182-daybtn"
                        tabIndex={dayKey === tabDay ? 0 : -1}
                        aria-pressed={open}
                        aria-label={t('ev182.cal.dayBtn', { day: dayWords, count: cell.events.length })}
                        onClick={() => onPick(dayKey)}
                        onKeyDown={onKey(dayKey)}
                        data-testid={`eg-daybtn-${dayKey}`}
                      >
                        <span className="tnum" aria-hidden="true">
                          {Number(dayKey.slice(6, 8))}
                        </span>
                      </button>
                      {/* on paper a button is not printed: the day's number is */}
                      <span className="ev182-printday tnum" aria-hidden="true">
                        {Number(dayKey.slice(6, 8))}
                      </span>
                      {today ? (
                        <span className="ev182-today" aria-hidden="true">
                          {t('ev182.cal.todayWord')}
                        </span>
                      ) : null}
                      <span id={`eg-dayname-${dayKey}`} hidden data-testid={`eg-dayname-${dayKey}`}>
                        {dayWords}
                      </span>
                      {onAdd && dayKey >= todayKey ? (
                        <button type="button" className="ev182-add no-print" tabIndex={-1} aria-label={t('ev182.cal.addOn', { day: dayWords })} title={t('ev182.cal.addOn', { day: dayWords })} onClick={(ev) => onAdd(dayKey, ev.currentTarget)} data-testid={`eg-add-${dayKey}`}>
                          <Icon name="plus" aria-hidden="true" />
                        </button>
                      ) : null}
                    </div>
                    {cell.events.length ? (
                      <ul className="plain-list ev182-lines" aria-label={t('ev182.cal.dayEvents', { day: dayWords })}>
                        {cell.events.slice(0, SHOWN).map((e) => {
                          const cancelled = e.status === 'Cancelled';
                          return (
                            <li key={e.eventId}>
                              <Link className={`ev182-line${cancelled ? ' is-cancelled' : ''}`} to={`/events/${encodeURIComponent(e.eventId)}`} aria-describedby={`eg-dayname-${dayKey}`} data-testid={`eg-ev-${e.eventId}`}>
                                {e.minutes === null ? null : <span className="tnum ev182-time">{clock(e.minutes)} </span>}
                                <span className="ev182-name">{e.name}</span>
                                {cancelled ? <span className="ev182-cancelled"> · {t('cal177m.cancelled')}</span> : null}
                              </Link>
                            </li>
                          );
                        })}
                        {extra > 0 ? (
                          <li>
                            <button type="button" className="ev182-more" tabIndex={-1} onClick={() => onPick(dayKey)} data-testid={`eg-more-${dayKey}`}>
                              {t('ev182.cal.more', { count: extra })}
                            </button>
                          </li>
                        ) : null}
                      </ul>
                    ) : null}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
      <p className="help ev182-keys" id="ev182-keys">
        {t('ev182.cal.keys')}
      </p>
    </div>
  );
}
